import React, { useEffect, useRef, useState } from "react";
import { Table, Card, Row, Col, DatePicker, Select, Button, Input } from "antd";
import axiosInstance from "../../variables/api/axiosInstance";
import { frenchDate } from "../../variables/functions";
import NavBar from "../../components/NavBar";
import SideBar from "../../components/SideBar";
import { FileExcelOutlined, FilePdfOutlined } from "@ant-design/icons";
import domToPdf from "dom-to-pdf";
import { useDownloadExcel } from "react-export-table-to-excel";
import dayjs from "dayjs";
import { displayNumbers } from "../../variables/numbersFunctions";

const { RangePicker } = DatePicker;

const Balance = () => {
    const [loading, setLoading] = useState(false);
    const [balanceData, setBalanceData] = useState([]);
    const [dateRange, setDateRange] = useState(null);
    const [selectedCompte, setSelectedCompte] = useState(null);
    const [comptes, setComptes] = useState([]);
    const [searchValue, setSearchValue] = useState("");
    const [exercice, setExercice] = useState(dayjs());
    const printReference = useRef();
    const tableRef = useRef();

    useEffect(() => {
        // Charger la liste des comptes
        axiosInstance
            .get("/api/compte")
            .then((res) => {
                const formattedComptes = res.data.map(item => ({
                    value: item.codeId,
                    text: `${item.numeroCompte}.${item.numeroCompagnie}.${item.numeroTiers}`
                }));
                setComptes(formattedComptes);
            })
            .catch((err) => {
                console.log(err);
            });
    }, []);

    useEffect(() => {
        if (dateRange) {
            setLoading(true);
            const startDate = dateRange[0].format("YYYY-MM-DD");
            const endDate = dateRange[1].format("YYYY-MM-DD");

            axiosInstance
                .get(`/api/details-ecriture/balance/${startDate}T00:00:00/${endDate}T23:59:59`)
                .then((res) => {
                    setBalanceData(res.data);
                    setLoading(false);
                })
                .catch((err) => {
                    console.log(err);
                    setLoading(false);
                });
        }
    }, [dateRange]);

    const generatePdf = () => {
        const element = document.querySelector(".summary-report-container");
        const options = {
            filename: "Balance.pdf",
        };
        return domToPdf(element, options);
    };

    const { onDownload } = useDownloadExcel({
        currentTableRef: tableRef.current,
        filename: "balanceComptesPoolIns.xlsx",
        sheet: "Balance",
    });

    const columns = [
        {
            title: "N° Compte",
            dataIndex: "numeroCompte",
            key: "numeroCompte",
        },
        {
            title: "Solde Ouverture",
            children: [
                {
                    title: "Débit",
                    dataIndex: "debitSoldeOuverture",
                    key: "debitSoldeOuverture",
                    render: (text) => text ? displayNumbers(Math.trunc(text)) : ""
                },
                {
                    title: "Crédit",
                    dataIndex: "creditSoldeOuverture",
                    key: "creditSoldeOuverture",
                    render: (text) => text ? displayNumbers(Math.trunc(text)) : ""
                }
            ]
        },
        {
            title: "Mouvement Période",
            children: [
                {
                    title: "Débit",
                    dataIndex: "debitMouvementPeriode",
                    key: "debitMouvementPeriode",
                    render: (text) => text ? displayNumbers(Math.trunc(text)) : ""
                },
                {
                    title: "Crédit",
                    dataIndex: "creditMouvementPeriode",
                    key: "creditMouvementPeriode",
                    render: (text) => text ? displayNumbers(Math.trunc(text)) : ""
                }
            ]
        },
        {
            title: "Solde Période",
            children: [
                {
                    title: "Débit",
                    dataIndex: "debitSoldePeriode",
                    key: "debitSoldePeriode",
                    render: (text) => text ? displayNumbers(Math.trunc(text)) : ""
                },
                {
                    title: "Crédit",
                    dataIndex: "creditSoldePeriode",
                    key: "creditSoldePeriode",
                    render: (text) => text ? displayNumbers(Math.trunc(text)) : ""
                }
            ]
        },
        {
            title: "Solde Final",
            children: [
                {
                    title: "Débit",
                    dataIndex: "debitSoldeFinal",
                    key: "debitSoldeFinal",
                    render: (text) => text ? displayNumbers(Math.trunc(text)) : ""
                },
                {
                    title: "Crédit",
                    dataIndex: "creditSoldeFinal",
                    key: "creditSoldeFinal",
                    render: (text) => text ? displayNumbers(Math.trunc(text)) : ""
                }
            ]
        }
    ];

    return (
        <div className="layout-wrapper layout-content-navbar">
            <div className="layout-container">
                <SideBar currentMenu={"COMPTABILITE"} />
                <div className="layout-page">
                    <NavBar />
                    <div className="content-wrapper">
                        <div className="container-xxl flex-grow-1 container-p-y">
                            <h4 className="fw-bold py-3 mb-4">Balance des comptes</h4>

                            <Card className="mb-4">
                                <Row gutter={16}>
                                    <Col span={6}>
                                        <DatePicker
                                            picker="year"
                                            value={exercice}
                                            onChange={setExercice}
                                            style={{ width: "100%" }}
                                        />
                                    </Col>
                                    <Col span={6}>
                                        <RangePicker
                                            style={{ width: "100%" }}
                                            onChange={setDateRange}
                                        />
                                    </Col>
                                    <Col span={6}>
                                        <Select
                                            showSearch
                                            style={{ width: "100%" }}
                                            placeholder="Point de vente"
                                            onChange={setSelectedCompte}
                                            options={comptes}
                                            filterOption={(input, option) =>
                                                (option?.text ?? "").toLowerCase().includes(input.toLowerCase())
                                            }
                                        />
                                    </Col>
                                    <Col span={6}>
                                        <Button
                                            type="primary"
                                            onClick={() => {
                                                setDateRange(null);
                                                setSelectedCompte(null);
                                                setBalanceData([]);
                                            }}
                                        >
                                            Réinitialiser
                                        </Button>
                                    </Col>
                                </Row>
                            </Card>

                            <Card>
                                <div className="summary-report-container" ref={printReference}>
                                    <Table
                                        columns={columns}
                                        dataSource={balanceData}
                                        loading={loading}
                                        rowKey="ligne"
                                        pagination={false}
                                        ref={tableRef}
                                    />
                                </div>
                            </Card>

                            <div className="mt-4">
                                <Button
                                    type="primary"
                                    icon={<FilePdfOutlined />}
                                    onClick={generatePdf}
                                    style={{ marginRight: 16, backgroundColor: "green" }}
                                >
                                    Générer PDF
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<FileExcelOutlined />}
                                    onClick={onDownload}
                                    style={{ backgroundColor: "green" }}
                                >
                                    Générer Excel
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Balance;
